import {
    FluentProvider,
    Text,
    tokens,
} from '@fluentui/react-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { customLightTheme, customDarkTheme } from './theme/customTheme'
import { useState, useEffect, useLayoutEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { ApiService } from './services/api'
import {
    AcceptInvitationPage,
    AppsPage,
    LoginPage,
    UserManagementPage,
    SelfServicePage,
    TenantManagementPage,
    TenantSecurityPage,
    SystemSettingsPage,
    OrganizationsPage,
    OrganizationDetailPage,
    PrivacyPage,
    SMSConsentPage,
    TermsPage,
    WorkspacesHomePage,
    WorkspaceDetailPage,
    MyOrganizationRedirect,
    ConsentCallbackPage,
    ConsentErrorPage,
    TemplatesPage,
} from './pages'
import { HomePage, PlatformPage, PricingPage } from './pages/marketing'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { Permission } from './types/auth'
import { TenantProvider, useTenantContext } from './contexts/TenantContext'
import { ProtectedRoute } from './components/Auth/ProtectedRoute'
import { AuthorizedRoute } from './components/Auth/AuthorizedRoute'
import { UserMenu } from './components/Auth/UserMenu'
import { Alert } from './components/common/Alert'
import { ThemeToggle } from './components/common/ThemeToggle'
import { AppLayout } from './components/Navigation/AppLayout'
import { msalConfig } from './config/msalConfig'

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig)

// Initialize React Query client with global defaults (configurable via env vars)
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: parseInt(import.meta.env.VITE_CACHE_STALE_TIME || '300000'), // Default 5 min
            gcTime: parseInt(import.meta.env.VITE_CACHE_GC_TIME || '600000'), // Default 10 min
            refetchOnWindowFocus: import.meta.env.VITE_CACHE_REFETCH_ON_FOCUS === 'true',
        },
    },
})

/**
 * Main application content with sidebar navigation, health check, and feature routes.
 *
 * Features organized as routes:
 * - /tenants: Tenant Management (global admins only)
 * - /users: User Management (with privilege management for superadmins)
 * - /security: Tenant Security (global admins only)
 * - /self-service: Self-Service (accessible via "My Profile" in avatar menu)
 * - /llm-settings: LLM Provider Settings (home tenant users only)
 *
 * Uses AppLayout component with left sidebar navigation.
 * Routes managed by React Router with permission-based access.
 */
interface AppContentProps {
    isDarkMode: boolean;
    onThemeToggle: () => void;
}

function AppContent({ isDarkMode, onThemeToggle }: AppContentProps) {
    const [healthStatus, setHealthStatus] = useState<string>('checking')
    const [healthError, setHealthError] = useState<string>('')
    const authContext = useAuth();
    const tenantContext = useTenantContext();

    // Set auth context on ApiService when it changes
    // Use useLayoutEffect to ensure auth context is set BEFORE child useEffects run
    // This prevents race conditions where child components make API calls before auth is ready
    useLayoutEffect(() => {
        ApiService.setAuthContext({
            getAccessToken: authContext.getAccessToken,
            isAuthenticated: authContext.isAuthenticated
        });
    }, [authContext.isAuthenticated, authContext.getAccessToken]);

    // Orchestrate tenant loading after authentication completes
    // This implements industry best practice: App coordinates provider timing,
    // providers remain independent and reusable (React Query/Redux/Apollo pattern)
    useEffect(() => {
        if (authContext.isAuthenticated) {
            tenantContext.refreshTenantList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authContext.isAuthenticated]);

    useEffect(() => {
        const checkBackendHealth = async () => {
            try {
                const response = await ApiService.checkHealth()
                if (response.status === 'healthy') {
                    setHealthStatus('connected')
                } else {
                    setHealthStatus('unhealthy')
                    setHealthError('Backend is running but reporting unhealthy status')
                }
            } catch (error) {
                // Health check failed - error state will be shown to user
                setHealthStatus('error')
                setHealthError('Unable to connect to backend. Make sure it\'s running on http://localhost:9000')
            }
        }

        checkBackendHealth()
        const interval = setInterval(checkBackendHealth, 30000)
        return () => clearInterval(interval)
    }, [])

    return (
        <AppLayout
            header={
                <>
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '20px', color: tokens.colorNeutralForeground1 }}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 48" width="150" height="36">
                            <defs>
                                <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" style={{ stopColor: '#0078d4' }} />
                                    <stop offset="100%" style={{ stopColor: '#005a9e' }} />
                                </linearGradient>
                            </defs>
                            <g transform="translate(4, 4)">
                                <path d="M20 0 L38 7 L38 20 C38 30 29 38 20 38 C11 38 2 30 2 20 L2 7 Z" fill="url(#iconGradient)" />
                                <path d="M12 36 L18 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                <path d="M28 36 L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                <line x1="20" y1="34" x2="20" y2="28" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round" />
                                <line x1="20" y1="24" x2="20" y2="20" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round" />
                                <line x1="20" y1="16" x2="20" y2="14" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round" />
                                <circle cx="20" cy="10" r="3" fill="white" />
                            </g>
                            <text x="50" y="32" fontFamily="Segoe UI, -apple-system, BlinkMacSystemFont, sans-serif" fontSize="24" fontWeight="600" fill="currentColor">
                                <tspan fill="#005a9e">IR</tspan><tspan fill="currentColor">Pilot</tspan>
                            </text>
                        </svg>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <ThemeToggle isDark={isDarkMode} onToggle={onThemeToggle} />
                        <UserMenu />
                    </div>
                </>
            }
        >
            <div style={{ padding: '16px 50px 50px', boxSizing: 'border-box' }}>
                {healthStatus === 'error' && (
                    <Alert intent="error">
                        <Text>Backend Connection Error: {healthError}</Text>
                    </Alert>
                )}

                {healthStatus === 'unhealthy' && (
                    <Alert intent="warning">
                        <Text>Backend Health Warning: {healthError}</Text>
                    </Alert>
                )}

                {healthStatus === 'connected' && (
                    <Routes>
                        {/* Default route - redirect to workspaces */}
                        <Route path="/" element={<Navigate to="workspaces" replace />} />

                        {/* Workspace routes */}
                        <Route path="/workspaces" element={<WorkspacesHomePage />} />
                        <Route path="/workspaces/:workspaceId" element={<WorkspaceDetailPage />} />

                        {/* Admin routes (new paths) */}
                        <Route
                            path="/admin/organizations"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_ALL_ORGANIZATIONS}>
                                    <OrganizationsPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route
                            path="/admin/organizations/:id"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_ORG_MEMBERSHIP}>
                                    <OrganizationDetailPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route
                            path="/admin/my-organization"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_ORG_MEMBERSHIP}>
                                    <MyOrganizationRedirect />
                                </AuthorizedRoute>
                            }
                        />
                        <Route
                            path="/admin/tenant-management"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_ALL_TENANTS}>
                                    <TenantManagementPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route
                            path="/admin/security"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_TENANT_SECURITY}>
                                    <TenantSecurityPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route
                            path="/admin/system-settings"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_LLM_SETTINGS}>
                                    <SystemSettingsPage />
                                </AuthorizedRoute>
                            }
                        />
                        {/* Redirect old LLM settings route to new System Settings */}
                        <Route
                            path="/admin/llm-settings"
                            element={<Navigate to="/admin/system-settings" replace />}
                        />

                        {/* Legacy routes (kept for backward compatibility) */}
                        <Route path="/apps" element={<AppsPage />} />
                        <Route
                            path="/organizations"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_ALL_ORGANIZATIONS}>
                                    <OrganizationsPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route
                            path="/organizations/:id"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_ORG_MEMBERSHIP}>
                                    <OrganizationDetailPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route
                            path="/tenants"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_ALL_TENANTS}>
                                    <TenantManagementPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route
                            path="/users"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_TENANT_USERS}>
                                    <UserManagementPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route
                            path="/security"
                            element={
                                <AuthorizedRoute requiredPermission={Permission.MANAGE_TENANT_SECURITY}>
                                    <TenantSecurityPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route path="/self-service" element={<SelfServicePage />} />
                        {/* Legacy redirect for old LLM settings route */}
                        <Route
                            path="/llm-settings"
                            element={<Navigate to="/admin/system-settings" replace />}
                        />

                        {/* Dev-only routes (only rendered in development mode) */}
                        {import.meta.env.DEV && (
                            <Route
                                path="/dev/templates"
                                element={
                                    <AuthorizedRoute requiredPermission={Permission.MANAGE_ALL_ORGANIZATIONS}>
                                        <TemplatesPage />
                                    </AuthorizedRoute>
                                }
                            />
                        )}
                    </Routes>
                )}
            </div>
        </AppLayout>
    )
}

/**
 * Root application component with providers and routing.
 *
 * Provider setup (Phase 4 MSAL Migration):
 * 1. BrowserRouter: React Router for navigation
 * 2. FluentProvider: Fluent UI theming (webLightTheme/webDarkTheme with dynamic switching)
 * 3. MsalProvider: MSAL authentication provider
 * 4. AuthProvider: Application authentication context (MSAL-based)
 * 5. TenantProvider: Tenant context for multi-tenant operations
 *
 * Provider Orchestration Pattern (Industry Best Practice):
 * - Providers are independent and don't depend on each other
 * - App.tsx (AppContent) coordinates timing between providers
 * - Authentication completes → App triggers tenant loading
 * - Follows React Query/Redux/Apollo pattern: components orchestrate, providers manage state
 *
 * Theme Management (Microsoft Fluent UI Approach):
 * - Simple state management at FluentProvider level (no separate ThemeContext)
 * - Theme persists to localStorage with key 'theme'
 * - Dynamic theme prop on FluentProvider switches between light and dark
 * - ThemeToggle button in header (next to UserMenu) for authenticated users
 *
 * Routes:
 * - /login: Login page
 * - /*: Protected routes with tab navigation
 */
function App() {
    // Theme state management with localStorage persistence
    const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
        const stored = localStorage.getItem('theme');
        return stored === 'dark';
    });

    // Persist theme preference to localStorage
    useEffect(() => {
        localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    // Toggle theme handler
    const handleThemeToggle = () => {
        setIsDarkMode(prev => !prev);
    };

    const currentTheme = isDarkMode ? customDarkTheme : customLightTheme;

    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <FluentProvider
                    theme={currentTheme}
                    className="fui-FluentProvider"
                >
                    <MsalProvider instance={msalInstance}>
                        <AuthProvider>
                            <TenantProvider>
                                <Routes>
                                    {/* Public marketing pages */}
                                    <Route path="/" element={<HomePage />} />
                                    <Route path="/platform" element={<PlatformPage />} />
                                    <Route path="/pricing" element={<PricingPage />} />

                                    {/* Auth and legal pages */}
                                    <Route path="/login" element={<LoginPage />} />
                                    <Route path="/auth/accept-invitation" element={<AcceptInvitationPage />} />
                                    <Route path="/auth/federated-consent-callback" element={<ConsentCallbackPage />} />
                                    <Route path="/organizations/consent-error" element={<ConsentErrorPage />} />
                                    <Route path="/privacy" element={<PrivacyPage />} />
                                    <Route path="/sms-terms" element={<SMSConsentPage />} />
                                    <Route path="/terms" element={<TermsPage />} />

                                    {/* Protected app routes under /app prefix */}
                                    <Route
                                        path="/app/*"
                                        element={
                                            <ProtectedRoute>
                                                <AppContent
                                                    isDarkMode={isDarkMode}
                                                    onThemeToggle={handleThemeToggle}
                                                />
                                            </ProtectedRoute>
                                        }
                                    />
                                </Routes>
                            </TenantProvider>
                        </AuthProvider>
                    </MsalProvider>
                </FluentProvider>
            </BrowserRouter>
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    )
}

export default App